<template>
  <SheetModal
    class="PlaceModalView"
    data-theme="light"
    fit="hug"
    :aria-label="modelValue.name"
    :aria-busy="isFetching || !isReady"
    @close="$emit('close')"
  >
    <figure class="PlaceModalView__mapFigure">
      <div class="PlaceModalView__mapContainer">
        <div
          ref="mapEl"
          class="PlaceModalView__map"
          data-testid="map"
        />
        <Transition>
          <EmptyState
            v-if="!isReady"
            class="PlaceModalView__mapEmptyState"
            iconName="locationMap"
          >
            <h3>Loading Google Maps…</h3>
            <p>This may take a few seconds</p>
          </EmptyState>
        </Transition>
      </div>
      <figcaption class="PlaceModalView__header">
        <h1 class="PlaceModalView__title">
          {{ location.name }}
        </h1>
        <p class="PlaceModalView__address">
          <Icon
            class="PlaceModalView__addressIcon"
            name="location"
            size="smedium"
          />
          <span class="PlaceModalView__addressLabelContainer">
            <Transition>
              <span
                :key="location.address ?? ''"
                class="PlaceModalView__addressLabel"
              >
                {{ location.address }}
              </span>
            </Transition>
          </span>
        </p>
      </figcaption>
    </figure>
    <div class="PlaceModalView__content">
      <FormButton
        iconName="location"
        :disabled="isSaving"
        @click="handleToggleClick"
      >
        <span v-if="!hasCourt && !isSaving">
          Add to your courts
        </span>
        <span v-else-if="!hasCourt && isSaving">
          Adding to your courts…
        </span>
        <span v-else-if="hasCourt && !isSaving">
          Remove from your courts
        </span>
        <span v-else-if="hasCourt && isSaving">
          Removing court…
        </span>
      </FormButton>
    </div>
  </SheetModal>
</template>

<script lang="ts" setup>
import EmptyState from "@/components/EmptyState.vue";
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import SheetModal from "@/components/SheetModal.vue";
import useGoogleMap from "@/composables/useGoogleMap";
import { supabaseClient } from "@/lib/database/supabase";
import haptic from "@/lib/utils/platform/haptic";
import useAuthStore from "@/stores/useAuthStore";
import useCommissionerCourtStore from "@/stores/useCommissionerCourtStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import { CourtSuggestion, Place, PlaceResponse, UpsertCourtRequest, UpsertCourtResponse } from "@shared/lib/api/courts.types";
import { Court } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";

const modelValue = defineModel<CourtSuggestion>({
  required: true,
});

const emit = defineEmits<{
  select: [Court],
  close: [],
}>();

const {
  sessionToken,
} = defineProps<{
  sessionToken?: string,
}>();

const {
  authHeaders,
} = storeToRefs(useAuthStore());

const {
  commissionerCrews,
} = storeToRefs(useCurrentPlayerStore());

const {
  addCommissionerCourt,
  removeCommissionerCourt,
} = useCommissionerCourtStore();

const {
  commissionerCourts,
} = storeToRefs(useCommissionerCourtStore());

const hasCourt = computed(() => {
  return commissionerCourts.value.some((court) => court.id === suggestedCourt.value?.id);
});

const isFetching = ref(false);
const fetchedPlace = ref<Place | null>(null);
const mapEl = ref<HTMLElement | null>(null);
const isSaving = ref(false);

onBeforeMount(async () => {
  if (suggestedCourt.value) {
    // TODO: fetch court crews
  } else {
    try {
      isFetching.value = true;
      await fetchPlace();
      isFetching.value = false;
    } catch (error) {
      alert((error as Error).message);
      emit("close");
    }
  }
});

const suggestedCourt = computed(() => {
  return modelValue.value.court;
});

const place = computed(() => {
  return fetchedPlace.value ?? {
    id: modelValue.value.placeId,
    name: modelValue.value.name,
    address: modelValue.value.address,
    latitude: null,
    longitude: null,
  };
});

const location = computed(() => {
  return suggestedCourt.value ?? place.value;
});

const coordinates = computed(() => {
  if (location.value.latitude == null || location.value.longitude == null) return null;

  return {
    latitude: location.value.latitude,
    longitude: location.value.longitude,
  };
});

const {
  isReady,
} = useGoogleMap(mapEl, coordinates);

const commissionerCrewIds = computed(() => {
  return commissionerCrews.value.map((crew) => crew.id);
});

const payload = computed((): UpsertCourtRequest | undefined => {
  return {
    google_place_id: place.value.id,
    crew_ids: commissionerCrewIds.value,
    session_token: sessionToken,
    name: place.value.name,
    address: place.value.address || undefined,
    latitude: place.value.latitude ?? undefined,
    longitude: place.value.longitude ?? undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
});

async function fetchPlace () {
  const query = new URLSearchParams();
  if (sessionToken) query.set("session_token", sessionToken);

  const response = await fetch(`/api/places/${modelValue.value.placeId}?${query}`, {
    headers: {
      ...authHeaders.value,
    },
  });

  if (!response.ok) throw new Error("Failed to load place");

  const {
    place,
  } = await response.json() as PlaceResponse;

  fetchedPlace.value = place;
}

async function upsertCourt () {
  isSaving.value = true;

  try {
    const response = await fetch("/api/courts", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders.value,
      },
      body: JSON.stringify(payload.value),
    });

    if (response.ok) {
      const {
        court,
      } = await response.json() as UpsertCourtResponse;
      modelValue.value.court = court;

      addCommissionerCourt(court);
      emit("select", court);
      emit("close");
      return court;
    }

    const {
      error,
    } = await response.json();
    alert(error?.message || "Something went wrong");
  } catch {
    alert("Something went wrong");
  } finally {
    isSaving.value = false;
  }
}

async function toggleCrewCourt () {
  if (!suggestedCourt.value) return;

  isSaving.value = true;
  const {
    error: syncError,
  } = await supabaseClient.rpc("sync_court_crews", {
    court_id: suggestedCourt.value.id,
    crew_ids: hasCourt.value ? [] : commissionerCrewIds.value,
  });

  if (syncError) {
    isSaving.value = false;
    alert(syncError.message);
    return;
  }

  const {
    data: court,
    error: fetchError,
  } = await supabaseClient.from("courts")
    .select("*")
    .eq("id", suggestedCourt.value.id)
    .maybeSingle();

  if (fetchError) {
    isSaving.value = false;
    alert(fetchError.message);
    return;
  }

  if (hasCourt.value) {
    isSaving.value = false;
    removeCommissionerCourt(suggestedCourt.value);
  } else if (court) {
    addCommissionerCourt(court);
    emit("select", court);
    emit("close");
    return court;
  }
}

async function handleToggleClick () {
  haptic();

  if (suggestedCourt.value) {
    await toggleCrewCourt();
  } else {
    await upsertCourt();
  }
}
</script>

<style scoped>
.PlaceModalView__mapFigure {
  overflow: hidden;
  border-radius: 16px 16px 0 0;
}

.PlaceModalView__mapContainer {
  position: relative;

  width: 100%;
  max-height: 300px;
  aspect-ratio: 4 / 3;
  overflow: hidden;

  background-color: var(--themeColorSurface);
}

.PlaceModalView__map {
  position: absolute;
  inset: 0;

  transition: 350ms var(--easeOutCubic);
  transition-property: opacity, transform;
  transition-delay: 150ms;

  .PlaceModalView[aria-busy="true"] & {
    transform: scale(1.25);
    opacity: 0;
  }
}

.PlaceModalView__mapEmptyState {
  position: absolute;
  inset: 0;

  transition: 350ms var(--easeOutCubic);
  transition-property: opacity, transform;
  transition-delay: 150ms;

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(0.9);
    opacity: 0;
  }
}

.PlaceModalView__header {
  display: grid;
  gap: 8px;
  margin: 1px 0;
  padding: 18px 16px 20px;

  outline: 1px solid var(--themeColorNeutral100);
  background-color: var(--themeColorWhite);
}

.PlaceModalView__title {
  font-size: 20px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  line-height: 1.2;
}

.PlaceModalView__address {
  display: grid;
  grid-template-columns: min-content auto;
  gap: 4px;
  align-items: center;

  color: var(--themeColorNeutral600);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.PlaceModalView__addressIcon {
  color: var(--themeColorNeutral500);
}

.PlaceModalView__addressLabelContainer {
  position: relative;
  top: calc(var(--fontSafariOffset) / 2);
  overflow: hidden;
}

.PlaceModalView__addressLabel {
  display: block;
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;

  transition: opacity 350ms;

  &.v-enter-active {
    transition-timing-function: ease-in-out;
    transition-delay: 350ms;
  }

  &.v-leave-active {
    position: absolute;
    top: 0;
    left: 0;

    transition-timing-function: ease-in-out;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.PlaceModalView__content {
  padding: 16px 16px calc(16px + var(--safeAreaInsetBottom));
}
</style>
